import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Card, CardContent, Chip, Typography, Button, IconButton, Tooltip,
} from '@mui/material';
import {
  Handshake as HandshakeIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  BusinessOutlined as AgencyIcon,
  EventOutlined as DeadlineIcon,
  OnlinePrediction as InterestIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, format, isPast } from 'date-fns';
import { fetcher } from 'shared/helpers/axios';

import { markdownToPlainText } from 'shared/utils/richTextMarkdown';
import { postPlainText } from 'shared/helpers/postText';
import { useTheme } from '@mui/material/styles';




// People without a photo: their initials on the app's avatar colour (as on posts and comments)
const AVATAR_SX = { bgcolor: 'primary.avatar', color: '#fff', fontWeight: 600 };

// ── Author row shown at the top of every tooltip ─────────────────────────────

function AuthorRow({ avatar, name, org }) {
  const initials = name ? name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase() : '?';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.6,
                pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
      <Avatar src={avatar || undefined} sx={{ width: 20, height: 20, fontSize: '0.6rem', ...AVATAR_SX }}>
        {!avatar && initials}
      </Avatar>
      {/* <Box> */}
        <Typography variant="caption" fontWeight={700} display="block">{name}</Typography>
        {org && (
          <Typography variant="caption" sx={{ opacity: 0.75, fontSize: '0.95rem' }}>{org}</Typography>
        )}
      {/* </Box> */}
    </Box>
  );
}

// ── Lazy call-details tooltip (includes author) ───────────────────────────────

function CallTooltip({ callId, authorAvatar, authorName, authorOrg, text, children }) {
  const [active, setActive] = useState(false);
  const { data: call } = useSWR(
    active && callId ? `/funding/calls/${callId}/` : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  const deadline = call?.closes_at ? new Date(call.closes_at) : null;

  return (
    <Tooltip
      arrow
      placement="left"
      enterDelay={300}
      onOpen={() => setActive(true)}
      title={
        <Box sx={{ p: 0.25, pb: 1, maxWidth: 260 }}>
          <AuthorRow avatar={authorAvatar} name={authorName} org={authorOrg} />
          {text && (
            <Typography variant="caption" display="block"
              sx={{ mb: 0.6, pb: 0.6, lineHeight: 1.5,
                    borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
              {text}
            </Typography>
          )}
          {!call ? (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>Loading call…</Typography>
          ) : (
            <>
              <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.3 }}>
                {call.title}
              </Typography>
              {call.agency_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.2 }}>
                  <AgencyIcon sx={{ fontSize: 11 }} />
                  <Typography variant="caption">{call.agency_name}</Typography>
                </Box>
              )}
              {deadline && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <DeadlineIcon sx={{ fontSize: 11 }} />
                  <Typography
                    variant="caption"
                    sx={{ color: isPast(deadline) ? 'error.light' : 'inherit' }}
                  >
                    {isPast(deadline) ? 'Closed' : `Closes ${format(deadline, 'dd MMM yyyy')}`}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
}

// ── Simple author + full-text tooltip (for posts/announcements) ───────────────

function AuthorTooltip({ avatar, name, org, text, children }) {
  return (
    <Tooltip
      arrow
      placement="left"
      enterDelay={300}
      title={
        <Box sx={{ p: 0.25, pb: 1, maxWidth: 260 }}>
          <AuthorRow avatar={avatar} name={name} org={org} />
          {text && (
            <Typography variant="caption" display="block" sx={{ lineHeight: 1.5 }}>
              {text.length > 300 ? `${text.slice(0, 300)}…` : text}
            </Typography>
          )}
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
}

// ── Main carousel card ────────────────────────────────────────────────────────

export default function InterestsNewsCard({ height = 200 }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);

const theme = useTheme();

  const SLIDES_CONFIG = [
    // Seeking Partners first, as on the Interests page (?tab= is the type_of_interest code)
    { key: 'partners',      label: 'Seeking Partners',   navPath: '/interests/?tab=1' },
    { key: 'consortium',     label: 'Seeking Consortium', navPath: '/interests/?tab=0' },
    { key: 'announcements', label: 'Announcements',   navPath: '/interests/?tab=2' },
  ];
  


  const { data: d0 } = useSWR(
    '/interact/comments/?target_type=call&ordering=-created&page_size=8&is_flagged=false&type_of_interest=0',
    fetcher, { revalidateOnFocus: false }
  );
  const { data: d1 } = useSWR(
    '/interact/comments/?target_type=call&ordering=-created&page_size=8&is_flagged=false&type_of_interest=1',
    fetcher, { revalidateOnFocus: false }
  );
  const { data: d2 } = useSWR(
    '/interact/posts/?is_announcement=true&is_flagged=false&ordering=-created&page_size=8',
    fetcher, { revalidateOnFocus: false }
  );

  const toList = (d) => d?.results ?? (Array.isArray(d) ? d : []);

  // Filter interest items that have no linked call (same rule as the page)
  const rawItems = [toList(d1), toList(d0), toList(d2)];   // in SLIDES_CONFIG order
  const slideItems = rawItems.map((items, i) =>
    i === 2 ? items : items.filter((item) => item.target_id && item.target_name)
  );

  // Only show a slide once its data has loaded AND it has items
  const visibleSlides = SLIDES_CONFIG
    .map((s, i) => ({ ...s, items: slideItems[i], loaded: [d1, d0, d2][i] !== undefined }))
    .filter(({ items, loaded }) => !loaded || items.length > 0);

  // Keep page in bounds when visible slides change
  useEffect(() => {
    if (visibleSlides.length > 0 && page >= visibleSlides.length) setPage(0);
  }, [visibleSlides.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (paused || visibleSlides.length === 0) return;
    const id = setInterval(() => setPage((p) => (p + 1) % visibleSlides.length), 3000);
    return () => clearInterval(id);
  }, [paused, visibleSlides.length]);

  const go = (dir) =>
    setPage((p) => (p + dir + visibleSlides.length) % visibleSlides.length);

  const getItemName = (item, isPost) =>
    isPost
      ? (item.author?.name || item.author_name || 'Someone')
      : ([item.author_info?.first_name, item.author_info?.last_name].filter(Boolean).join(' ') || item.author_info?.username || 'Someone');

  const getItemAvatar = (item, isPost) =>
    isPost ? (item.author?.avatar || null) : (item.author_info?.avatar || null);

  const getItemOrg = (item, isPost) => {
    // authors without an organization (or without a profile at all) have none to show
    const org = (isPost ? item.author?.organization_name : item.author_info?.organization_name) || '';
    return org.length > 35 ? org.slice(0, 35) + '...' : org;
  }
  return (
    <Card
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      sx={{
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.08)',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
      }}
    >
      <CardContent sx={{ pb: '12px !important', px: 1.5 }}>
        {/* Header */}
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.25 }}>
          <InterestIcon sx={{ fontSize: 20, color: 'primary.light' }} />
          Interests
        </Typography>

        {/* Carousel */}
        <Box sx={{ position: 'relative', height }}>
          <IconButton
            size="small"
            onClick={() => go(-1)}
            sx={{
              position: 'absolute', left: -14, top: '50%', transform: 'translateY(-50%)',
              zIndex: 2, bgcolor: 'background.paper', boxShadow: 2, p: '2px',
              '&:hover': { bgcolor: 'background.paper', boxShadow: 3 },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <Box sx={{ overflow: 'hidden', mx: '18px' }}>
            <Box
              sx={{
                display: 'flex',
                transform: `translateX(-${page * 100}%)`,
                transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {visibleSlides.map((s) => {
                const { items } = s;
                const isPost = s.key === 'announcements';
                return (
                  <Box key={s.key} sx={{ minWidth: '100%' }}>
                    {/* Slide label + See all */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      {/* <Typography variant="caption" sx={{ fontWeight: 700, color: s.color, lineHeight: 1,  }}>
                        {s.label}
                      </Typography> */}


<Chip
      // icon={info.icon}
      label={s.label}
      size="small"
      sx={{
        bgcolor: theme.palette.divider, 
        color: "text.primary",
        fontWeight: 300,
        // '& .MuiChip-icon': { color: s.color },
      }}
    />


                      <Button
                        size="small"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '11px !important' }} />}
                        onClick={() => navigate(s.navPath)}
                        sx={{ textTransform: 'none', fontSize: '0.95rem', minWidth: 0, py: 0, lineHeight: 1 }}
                      >
                        Show all
                      </Button>
                    </Box>

                    {/* Scrollable content */}
                    <Box
                      sx={{
                        maxHeight: height - 32, overflowY: 'auto', pr: 0.25,
                        '&::-webkit-scrollbar': { width: 1  },
                        // '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: 4 },
                      }}
                    >
                      {items.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">Nothing here yet.</Typography>
                      ) : items.map((item) => {
                        const name = getItemName(item, isPost);
                        const avatar = getItemAvatar(item, isPost);
                        const org = getItemOrg(item, isPost);
                        const initials = name ? name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase() : '?';
                        // announcements and interests are both markdown: the preview shows their words only
                        const text = isPost ? (item.title || postPlainText(item.body || '')) : postPlainText(markdownToPlainText(item.body || ''));
                        const timeAgo = item.created
                          ? formatDistanceToNow(new Date(item.created), { addSuffix: true })
                          : '';

                        const inner = (
                          <Box
                            key={item.id}
                            onClick={() => navigate(isPost && item.position ? `/positions/${item.position}/` : s.navPath)}
                            sx={{
                              py: 0.5, cursor: 'pointer',
                              // borderBottom: '1px solid', borderColor: 'divider',
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { bgcolor: 'action.hover' },
                              px: 0.5, borderRadius: 1.5,
                              display: 'flex', alignItems: 'flex-start', gap: 0.75,
                            }}
                          >
                            <Avatar
                              src={avatar || undefined}
                              sx={{ width: 22, height: 22, fontSize: '0.58rem', mt: '1px', flexShrink: 0, ...AVATAR_SX }}
                            >
                              {!avatar && initials}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                color="text.primary"
                                sx={{ lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                              >
                                {text}
                              </Typography>
                              {timeAgo && (
                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.72rem', mt: 0.1 }}>
                                  {timeAgo}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );

                        return !isPost && item.target_id ? (
                          <CallTooltip key={item.id} callId={item.target_id}
                            authorAvatar={avatar} authorName={name} authorOrg={org} text={text}>
                            {inner}
                          </CallTooltip>
                        ) : (
                          <AuthorTooltip key={item.id} avatar={avatar} name={name} org={org} text={text}>
                            {inner}
                          </AuthorTooltip>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={() => go(1)}
            sx={{
              position: 'absolute', right: -14, top: '50%', transform: 'translateY(-50%)',
              zIndex: 2, bgcolor: 'background.paper', boxShadow: 2, p: '2px',
              '&:hover': { bgcolor: 'background.paper', boxShadow: 3 },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Dot indicators — only for visible slides */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 1.25 }}>
          {visibleSlides.map((s, i) => (
            <Box
              key={s.key}
              onClick={() => setPage(i)}
              sx={{
                height: 5,
                width: i === page ? 18 : 5,
                borderRadius: 3,
                bgcolor: i === page ? 'primary.main' : 'action.disabled',
                cursor: 'pointer',
                transition: 'all 0.35s ease',
              }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
